import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  User, Mail, Phone, Building2, MapPin, CreditCard, CheckCircle, 
  AlertCircle, Loader2, X, Calendar, Clock, ArrowLeft
} from 'lucide-react';
import { getEvent, createEventRegistration, DEFAULT_BASE_URL } from '../services/api';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { isEventExpired as isEventExpiredUtil, getEventRegistrationState } from '../utils/eventDateTime';

const EventRegistrationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);
  
  const [formData, setFormData] = useState({
    participantName: '',
    participantEmail: '',
    participantMobile: '',
    organizationName: '',
    designation: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    specialRequirements: '',
    dietaryPreferences: ''
  });

  useEffect(() => {
    const loadEvent = async () => {
      try {
        if (!id) {
          setErrorMessage('Invalid event ID. Please check the URL and try again.');
          setLoading(false);
          return;
        }
        
        const found = await getEvent(id);
        setErrorMessage('');
        setEvent(found || null);
        
        // Check payment status from URL
        const status = searchParams.get('status');
        if (status === 'success') {
          setPaymentStatus('success');
        } else if (status === 'failed') {
          setPaymentStatus('failed');
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load event:', err);
        }
        if (err?.status === 404) {
          setErrorMessage(`Event not found. The event ID "${id}" does not exist or may have been removed.`);
        } else {
          setErrorMessage('Failed to load event details. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    loadEvent();
  }, [id, searchParams]);

  const isEventExpired = () => {
    if (!event) return false;
    return isEventExpiredUtil(event);
  };


  const canRegister = () => {
    if (!event) return false;
    if (!event.isPublished) return false;
    if (!event.registrationRequired) return false;
    if (event.status === 'Cancelled') return false;
    const { state, start, end } = getEventRegistrationState(event);
    if (state !== 'open') return false;
    const hasRegistrationWindow = Boolean(start || end);
    if (!hasRegistrationWindow) {
      if (event.status === 'Completed') return false;
      if (isEventExpired()) return false;
    }
    if (event.maxParticipants && event.currentParticipants >= event.maxParticipants) return false;
    return true;
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'participantMobile':
        if (!value.trim()) {
          error = getText('eventRegistration.mobileRequired');
        } else if (!/^[6-9]\d{9}$/.test(value.trim())) {
          error = getText('eventRegistration.mobileInvalid');
        }
        break;
      case 'participantEmail':
        if (!value.trim()) {
          error = getText('eventRegistration.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          error = getText('eventRegistration.emailInvalid');
        }
        break;
      case 'participantName':
        if (!value.trim()) {
          error = getText('eventRegistration.nameRequired');
        } else if (value.trim().length < 2) {
          error = getText('eventRegistration.nameMinLength');
        }
        break;
      case 'pincode':
        if (value && !/^\d{6}$/.test(value.trim())) {
          error = getText('eventRegistration.pincodeInvalid');
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-format certain fields
    let processedValue = value;
    if (name === 'participantMobile' || name === 'pincode') {
      processedValue = value.replace(/\D/g, ''); // Only numbers
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setErrorMessage('');
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['participantName', 'participantEmail', 'participantMobile'];
    
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    // Validate optional fields if they have values
    if (formData.pincode && formData.pincode.trim()) {
      const error = validateField('pincode', formData.pincode);
      if (error) {
        newErrors.pincode = error;
      }
    }
    
    setErrors(newErrors);
    const keys = Object.keys(newErrors);
    return { ok: keys.length === 0, fieldErrors: newErrors, firstErrorField: keys[0] };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setErrors({});
    
    // Check if event is valid for registration
    if (!canRegister()) {
      const { state, start, end } = getEventRegistrationState(event);
      const hasRegistrationWindow = Boolean(start || end);
      if (!event.isPublished) {
        setErrorMessage('This event is not currently available for registration.');
      } else if (event.status === 'Cancelled') {
        setErrorMessage('This event has been cancelled and is not accepting registrations.');
      } else if (state === 'not_open' && start) {
        setErrorMessage(`Event registration will open on ${start.toLocaleString()}.`);
      } else if (state === 'closed' && end) {
        setErrorMessage(`Event registration closed on ${end.toLocaleString()}.`);
      } else if (state === 'misconfigured') {
        setErrorMessage('Event registration is not available due to a configuration issue. Please contact support.');
      } else if (!hasRegistrationWindow && event.status === 'Completed') {
        setErrorMessage('This event is already completed and registration is closed.');
      } else if (!hasRegistrationWindow && isEventExpired()) {
        setErrorMessage('Event registration has closed. The event date has passed.');
      } else if (event.maxParticipants && event.currentParticipants >= event.maxParticipants) {
        setErrorMessage('Event has reached maximum participants.');
      } else {
        setErrorMessage('Registration is not available for this event.');
      }
      return;
    }
    
    const validation = validateForm();
    if (!validation.ok) {
      setErrorMessage('Please fix the errors in the form before submitting.');
      const firstErrorField = validation.firstErrorField;
      if (firstErrorField) {
        document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        document.getElementById(firstErrorField)?.focus();
      }
      return;
    }
    
    setSubmitting(true);
    setPaymentStatus(null);
    
    try {
      // Validate event exists
      if (!event) {
        setErrorMessage('Event not found. Please refresh the page and try again.');
        setSubmitting(false);
        return;
      }
      
      // Get eventId - ensure it's a string
      const eventId = String(event._id || event.id || '');
      if (!eventId) {
        setErrorMessage('Invalid event ID. Please refresh the page and try again.');
        setSubmitting(false);
        return;
      }
      
      const payload = {
        eventId: eventId,
        participantName: formData.participantName.trim(),
        participantEmail: formData.participantEmail.toLowerCase().trim(),
        participantMobile: formData.participantMobile.trim(),
        organizationName: formData.organizationName?.trim() || '',
        designation: formData.designation?.trim() || '',
        address: formData.address?.trim() || '',
        city: formData.city?.trim() || '',
        state: formData.state?.trim() || '',
        pincode: formData.pincode?.trim() || '',
        specialRequirements: formData.specialRequirements?.trim() || '',
        dietaryPreferences: formData.dietaryPreferences?.trim() || ''
      };
      
      const registration = await createEventRegistration(payload);
      const registrationFee = event.fees?.amount || 0;
      
      // If there's a registration fee, proceed to payment
      if (registrationFee > 0) {
        const baseUrl = DEFAULT_BASE_URL.replace(/\/$/, '');
        const eventRegistrationId = registration._id || registration.id;
        const eventIdForPayment = event._id || event.id;
        const idempotencyKey = `event:${eventRegistrationId}:${eventIdForPayment}`;

        const res = await fetch(`${baseUrl}/payments/create-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventRegistrationId,
            amount: registrationFee,
            eventTitle: event.title,
            eventId: eventIdForPayment,
            idempotencyKey,
            notes: {
              participantEmail: formData.participantEmail,
              participantMobile: formData.participantMobile
            }
          })
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              errorData.error ||
              'Failed to generate payment link. Please try again.'
          );
        }

        const data = await res.json();
        if (!data?.order?.id) {
          throw new Error('Payment order not generated. Please try again.');
        }

        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded || !window.Razorpay) {
          throw new Error('Unable to load Razorpay checkout. Please check your network and try again.');
        }

        const razorpayKeyId = (data.keyId && String(data.keyId).trim()) || process.env.REACT_APP_RAZORPAY_KEY_ID;
        if (!razorpayKeyId) {
          throw new Error('Payment gateway is not configured. Please contact support.');
        }
        if (process.env.NODE_ENV === 'development' && /^rzp_(live|test)_xxxxxx$/.test(razorpayKeyId)) {
          console.warn(
            '[Razorpay] Key ID is still a placeholder (rzp_live_xxxxxx / rzp_test_xxxxxx). ' +
            'Set RAZORPAY_KEY_ID in backend .env or REACT_APP_RAZORPAY_KEY_ID for the client.'
          );
        }

        setPaymentStatus('redirecting');

        const razorpay = new window.Razorpay({
          key: razorpayKeyId,
          amount: data.order.amount,
          currency: data.order.currency || 'INR',
          name: 'Industrial Federation of Maharashtra Region',
          description: `Event Registration - ${event.title}`,
          order_id: data.order.id,
          prefill: {
            name: formData.participantName,
            email: formData.participantEmail,
            contact: formData.participantMobile
          },
          theme: {
            color: '#3399cc'
          },
          modal: {
            ondismiss: () => {
              setPaymentStatus('failed');
              setErrorMessage('Payment was cancelled. You can try again anytime.');
            }
          },
          handler: async (response) => {
            try {
              const verifyRes = await fetch(`${baseUrl}/payments/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(response)
              });

              const verifyData = await verifyRes.json().catch(() => ({}));
              if (!verifyRes.ok || !verifyData.success) {
                throw new Error(verifyData.message || 'Payment verification failed. Please contact support.');
              }

              setPaymentStatus('success');
              setErrorMessage('');
            } catch (verifyError) {
              setPaymentStatus('failed');
              setErrorMessage(verifyError.message || 'Payment verification failed. Please contact support.');
            }
          }
        });

        razorpay.on('payment.failed', (response) => {
          setPaymentStatus('failed');
          const reason =
            response?.error?.description ||
            response?.error?.reason ||
            'Payment failed. Please try again.';
          setErrorMessage(reason);
        });

        razorpay.open();
        return;
      } else {
        // Free event - registration confirmed
        setPaymentStatus('success');
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Registration error:', err);
      }
      
      let errorMsg = 'Registration failed. Please try again.';
      if (err.message) {
        errorMsg = err.message;
      } else if (err.data) {
        errorMsg = err.data.error || err.data.message || errorMsg;
      } else if (err.response && err.response.data) {
        errorMsg = err.response.data.message || err.response.data.error || errorMsg;
      }
      
      // More specific error messages
      if (err.status === 404) {
        if (err.data && err.data.eventId) {
          errorMsg = `Event not found. The event ID "${err.data.eventId}" does not exist in the database.`;
        } else {
          errorMsg = 'Event not found. Please check the event ID and try again.';
        }
      } else if (err.status === 400) {
        // Check for duplicate registration error - only show if backend explicitly blocks
        if (err.message && (err.message.includes('already successfully registered') || err.message.includes('already registered'))) {
          // Only show error if backend explicitly set allowReregister to false
          if (err.data && err.data.allowReregister === false) {
            if (err.data && err.data.message) {
              errorMsg = err.data.message;
            } else if (err.data && (err.data.status === 'confirmed' || err.data.status === 'attended')) {
              errorMsg = 'You have already successfully registered for this event. Your registration is confirmed. If you need to make changes, please contact support.';
            } else {
              errorMsg = err.data.error || err.message || errorMsg;
            }
          } else {
            // If backend allows re-registration, don't show error - registration should proceed
            // This shouldn't happen, but if it does, just use the backend error message
            errorMsg = err.data?.error || err.message || errorMsg;
          }
        } else if (err.data && err.data.registrationOpensAt) {
          const opensAtRaw = err.data.registrationOpensAt;
          const opensAtDate = new Date(opensAtRaw);
          if (!Number.isNaN(opensAtDate.getTime())) {
            errorMsg = `Event registration will open on ${opensAtDate.toLocaleString()}.`;
          } else {
            errorMsg = `Event registration has not opened yet. Opens at ${opensAtRaw}.`;
          }
        } else if (err.data && err.data.registrationClosedAt) {
          const closesAtRaw = err.data.registrationClosedAt;
          const closesAtDate = new Date(closesAtRaw);
          if (!Number.isNaN(closesAtDate.getTime())) {
            errorMsg = `Event registration closed on ${closesAtDate.toLocaleString()}.`;
          } else {
            errorMsg = `Event registration has closed. Closed at ${closesAtRaw}.`;
          }
        } else if (err.data && err.data.received) {
          errorMsg = `Invalid event ID format: "${err.data.received}". Please refresh the page and try again.`;
        } else if (err.data && err.data.details) {
          const missing = Object.entries(err.data.details)
            .filter(([key, value]) => !value)
            .map(([key]) => key)
            .join(', ');
          if (missing) {
            errorMsg = `Missing required fields: ${missing}`;
          } else {
            errorMsg = err.message || errorMsg;
          }
        } else {
          // Keep the error message from backend as it's usually specific
          errorMsg = err.message || errorMsg;
        }
      } else if (err.status === 500) {
        errorMsg = 'Server error. Please try again later or contact support.';
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Registration error details:', {
          status: err.status,
          message: err.message,
          data: err.data,
          fullError: err
        });
      }
      
      const normalizedMsg = String(errorMsg || '').toLowerCase();
      const isAlreadyConfirmedRegistration =
        normalizedMsg.includes('already successfully registered') ||
        normalizedMsg.includes('already registered') ||
        normalizedMsg.includes('registration is confirmed');

      if (isAlreadyConfirmedRegistration) {
        setPaymentStatus('success');
        setErrorMessage('');
      } else {
        setPaymentStatus('failed');
        setErrorMessage(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return String(dateValue);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center py-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h2>
            <p className="text-gray-600 mb-6">The event you're looking for doesn't exist.</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/events')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg"
              >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">Back to Events</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canRegister()) {
    const { state, start, end } = getEventRegistrationState(event);
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Not Available</h2>
            {isEventExpired() ? (
              <p className="text-gray-600 mb-6">This event has already passed. Registration is closed.</p>
            ) : !event.isPublished ? (
              <p className="text-gray-600 mb-6">This event is not currently open for registration.</p>
            ) : state === 'not_open' && start ? (
              <p className="text-gray-600 mb-6">Registration opens on {formatDateTime(start)}.</p>
            ) : state === 'closed' && end ? (
              <p className="text-gray-600 mb-6">Registration closed on {formatDateTime(end)}.</p>
            ) : state === 'misconfigured' ? (
              <p className="text-gray-600 mb-6">Registration is unavailable due to a configuration issue. Please contact support.</p>
            ) : event.maxParticipants && event.currentParticipants >= event.maxParticipants ? (
              <p className="text-gray-600 mb-6">This event has reached maximum participants.</p>
            ) : (
              <p className="text-gray-600 mb-6">Registration is not available for this event.</p>
            )}

            <div className="mt-6">
              <button
                onClick={() => navigate(`/events/${id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg"
              >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">Back to Event Details</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const registrationFee = event.fees?.amount || 0;
  const eventDate = event.endDate || event.startDate || event.date;
  const { start: registrationStart, end: registrationEnd } = getEventRegistrationState(event);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary-100 rounded-full">
                <Calendar className="h-8 w-8 text-primary-600" />
              </div>
              <span className="text-3xl font-bold text-gray-900">IFM</span>
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Event Registration
          </h1>
          <p className="text-lg text-gray-600">
            Register for: <span className="font-semibold text-primary-600">{event.title}</span>
          </p>
          {eventDate && (
            <p className="text-sm text-gray-500 mt-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              {formatDate(eventDate)}
            </p>
          )}
          {(registrationStart || registrationEnd) && (
            <p className="text-sm text-gray-500 mt-2">
              <Clock className="inline h-4 w-4 mr-1" />
              {registrationStart && registrationEnd
                ? `Registration: ${formatDateTime(registrationStart)} - ${formatDateTime(registrationEnd)}`
                : registrationStart
                  ? `Registration opens: ${formatDateTime(registrationStart)}`
                  : `Registration closes: ${formatDateTime(registrationEnd)}`}
            </p>
          )}
        </div>

        {/* Payment Status Display - Success */}
        {paymentStatus === 'success' && (
          <div className="mb-8 bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-2xl shadow-xl border-2 border-green-200 overflow-hidden">
            <div className="p-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 p-4 bg-green-500 rounded-full shadow-lg">
                  <CheckCircle className="h-12 w-12 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Registration Successful!</h2>
                <p className="text-lg text-gray-700 mb-6 max-w-2xl">
                  {registrationFee > 0 
                    ? 'Your payment has been processed successfully and your registration is confirmed. You will receive a confirmation email shortly.'
                    : 'Your registration has been confirmed successfully. You will receive a confirmation email shortly.'}
                </p>
                
                {/* Registration Details Card */}
                <div className="w-full max-w-md bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">Registration Details</h3>
                  <div className="space-y-3 text-left">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Event:</span>
                      <span className="text-sm font-semibold text-gray-900 text-right">{event.title}</span>
                    </div>
                    {eventDate && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">Date:</span>
                        <span className="text-sm font-semibold text-gray-900">{formatDate(eventDate)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Participant:</span>
                      <span className="text-sm font-semibold text-gray-900 text-right">{formData.participantName || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Email:</span>
                      <span className="text-sm font-semibold text-gray-900 text-right break-all">{formData.participantEmail || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Mobile:</span>
                      <span className="text-sm font-semibold text-gray-900">{formData.participantMobile || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <span className="text-sm font-medium text-gray-600">Amount Paid:</span>
                      <span className="text-lg font-bold text-primary-600">
                        {registrationFee > 0 ? `₹${registrationFee.toLocaleString('en-IN')}` : 'Free'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                  <button
                    onClick={() => navigate(`/events/${id}`)}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg font-semibold transform hover:scale-105"
                  >
                    View Event Details
                  </button>
                  <button
                    onClick={() => navigate('/events')}
                    className="flex-1 px-6 py-3 bg-white text-primary-600 border-2 border-primary-600 rounded-lg hover:bg-primary-50 transition-all font-semibold transform hover:scale-105"
                  >
                    Browse More Events
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {paymentStatus === 'failed' && (
          <div className="mb-8 bg-gradient-to-br from-red-50 via-rose-50 to-red-100 rounded-2xl shadow-xl border-2 border-red-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start">
                <div className="p-3 bg-red-500 rounded-full mr-4 flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-red-900 mb-2">Registration Failed</h3>
                  <p className="text-base text-red-700 mb-4">
                    There was an issue processing your registration. Please check the error message below and try again.
                  </p>
                  {errorMessage && (
                    <div className="bg-white rounded-lg p-3 border border-red-200">
                      <p className="text-sm text-red-800 font-medium">{errorMessage}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {paymentStatus === 'redirecting' && (
          <div className="mb-8 bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100 rounded-2xl shadow-xl border-2 border-blue-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="mb-4 inline-block p-4 bg-blue-500 rounded-full">
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">{getText('eventRegistration.redirectingToPaymentGateway')}</h3>
                  <p className="text-base text-blue-700">
                    {getText('eventRegistration.pleaseWaitRedirect')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && paymentStatus !== 'failed' && (
          <div className="mb-6 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl shadow-md overflow-hidden">
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <div className="p-2 bg-red-500 rounded-lg mr-3 flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-red-900 mb-1">Error</h3>
                    <p className="text-sm text-red-800 leading-relaxed">{errorMessage}</p>
                  </div>
                </div>
                <button
                  onClick={() => setErrorMessage('')}
                  className="text-red-400 hover:text-red-600 ml-3 p-1 rounded-lg hover:bg-red-100 transition-colors flex-shrink-0"
                  aria-label="Dismiss error"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Event Summary Card */}
        {paymentStatus !== 'success' && (
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border-2 border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-primary-200">
              <Calendar className="h-5 w-5 text-primary-600" />
              <h2 className="text-xl font-bold text-gray-900">Event Summary</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-2">
                <span className="text-sm font-medium text-gray-600 min-w-[100px]">Event:</span>
                <span className="text-sm font-semibold text-gray-900 leading-relaxed">{event.title}</span>
              </div>
              {eventDate && (
                <div className="flex items-start gap-2">
                  <span className="text-sm font-medium text-gray-600 min-w-[100px]">Date:</span>
                  <span className="text-sm font-semibold text-gray-900">{formatDate(eventDate)}</span>
                </div>
              )}
              {event.location && (
                <div className="flex items-start gap-2">
                  <span className="text-sm font-medium text-gray-600 min-w-[100px]">Location:</span>
                  <span className="text-sm font-semibold text-gray-900 leading-relaxed">{event.location}</span>
                </div>
              )}
              <div className="flex items-start gap-2">
                <span className="text-sm font-medium text-gray-600 min-w-[100px]">Registration Fee:</span>
                <span className={`text-sm font-bold ${registrationFee > 0 ? 'text-primary-600' : 'text-green-600'}`}>
                  {registrationFee > 0 ? `₹${registrationFee.toLocaleString('en-IN')}` : 'Free'}
                </span>
              </div>
              {event.maxParticipants && (
                <div className="flex items-start gap-2 md:col-span-2">
                  <span className="text-sm font-medium text-gray-600 min-w-[100px]">Participants:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {event.currentParticipants || 0} / {event.maxParticipants} registered
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form */}
        {paymentStatus !== 'success' && (
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8">
            {/* Participant Details Section */}
            <div>
              <div className="flex items-center gap-2 mb-6 pb-3 border-b-2 border-primary-200">
                <User className="h-5 w-5 text-primary-600" />
                <h2 className="text-xl font-bold text-gray-900">Participant Details</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div>
                  <label htmlFor="participantName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="participantName"
                      name="participantName"
                      type="text"
                      required
                      value={formData.participantName}
                      onChange={handleChange}
                      placeholder={getText('eventRegistration.namePlaceholder')}
                      className={`appearance-none block w-full pl-10 pr-4 py-3 border-2 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all ${
                        errors.participantName ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                    />
                  </div>
                  {errors.participantName && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5">
                      <AlertCircle className="h-4 w-4" />
                      {errors.participantName}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="participantEmail" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="participantEmail"
                      name="participantEmail"
                      type="email"
                      required
                      value={formData.participantEmail}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      className={`appearance-none block w-full pl-10 pr-4 py-3 border-2 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all ${
                        errors.participantEmail ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                    />
                  </div>
                  {errors.participantEmail && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5">
                      <AlertCircle className="h-4 w-4" />
                      {errors.participantEmail}
                    </p>
                  )}
                </div>

                {/* Mobile */}
                <div>
                  <label htmlFor="participantMobile" className="block text-sm font-semibold text-gray-700 mb-2">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="participantMobile"
                      name="participantMobile"
                      type="tel"
                      required
                      value={formData.participantMobile}
                      onChange={handleChange}
                      placeholder="Enter 10-digit mobile number"
                      maxLength={10}
                      className={`appearance-none block w-full pl-10 pr-4 py-3 border-2 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all ${
                        errors.participantMobile ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                    />
                  </div>
                  {errors.participantMobile && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5">
                      <AlertCircle className="h-4 w-4" />
                      {errors.participantMobile}
                    </p>
                  )}
                </div>

                {/* Organization */}
                <div>
                  <label htmlFor="organizationName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Organization Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building2 className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="organizationName"
                      name="organizationName"
                      type="text"
                      value={formData.organizationName}
                      onChange={handleChange}
                      placeholder="Enter organization name (optional)"
                      className="appearance-none block w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all bg-white"
                    />
                  </div>
                </div>

                {/* Designation */}
                <div>
                  <label htmlFor="designation" className="block text-sm font-semibold text-gray-700 mb-2">
                    Designation
                  </label>
                  <input
                    id="designation"
                    name="designation"
                    type="text"
                    value={formData.designation}
                    onChange={handleChange}
                    placeholder="Enter your designation (optional)"
                    className="appearance-none block w-full pl-4 pr-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div>
              <div className="flex items-center gap-2 mb-6 pb-3 border-b-2 border-primary-200">
                <MapPin className="h-5 w-5 text-primary-600" />
                <h2 className="text-xl font-bold text-gray-900">Address Details</h2>
              </div>
              
              <div className="space-y-6">
                {/* Address */}
                <div>
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                    Address
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <MapPin className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      id="address"
                      name="address"
                      rows={3}
                      value={formData.address}
                      onChange={handleChange}
                      placeholder={getText('eventRegistration.addressPlaceholder')}
                      className="appearance-none block w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all resize-none bg-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* City */}
                  <div>
                    <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Enter city (optional)"
                      className="appearance-none block w-full pl-4 pr-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all bg-white"
                    />
                  </div>
                  
                  {/* State */}
                  <div>
                    <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      id="state"
                      name="state"
                      type="text"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="Enter state (optional)"
                      className="appearance-none block w-full pl-4 pr-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all bg-white"
                    />
                  </div>
                  
                  {/* Pincode */}
                  <div>
                    <label htmlFor="pincode" className="block text-sm font-semibold text-gray-700 mb-2">
                      Pincode
                    </label>
                    <input
                      id="pincode"
                      name="pincode"
                      type="text"
                      value={formData.pincode}
                      onChange={handleChange}
                      placeholder="Enter 6-digit pincode (optional)"
                      maxLength={6}
                      className={`appearance-none block w-full pl-4 pr-4 py-3 border-2 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all ${
                        errors.pincode ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                    />
                    {errors.pincode && (
                      <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5">
                        <AlertCircle className="h-4 w-4" />
                        {errors.pincode}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information Section */}
            <div>
              <div className="flex items-center gap-2 mb-6 pb-3 border-b-2 border-primary-200">
                <CreditCard className="h-5 w-5 text-primary-600" />
                <h2 className="text-xl font-bold text-gray-900">Additional Information</h2>
              </div>
              
              <div className="space-y-6">
                {/* Special Requirements */}
                <div>
                  <label htmlFor="specialRequirements" className="block text-sm font-semibold text-gray-700 mb-2">
                    Special Requirements
                  </label>
                  <textarea
                    id="specialRequirements"
                    name="specialRequirements"
                    rows={3}
                    value={formData.specialRequirements}
                    onChange={handleChange}
                    placeholder="Any special requirements or accommodations needed (optional)"
                    className="appearance-none block w-full pl-4 pr-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all resize-none bg-white"
                  />
                </div>
                
                {/* Dietary Preferences */}
                <div>
                  <label htmlFor="dietaryPreferences" className="block text-sm font-semibold text-gray-700 mb-2">
                    Dietary Preferences
                  </label>
                  <input
                    id="dietaryPreferences"
                    name="dietaryPreferences"
                    type="text"
                    value={formData.dietaryPreferences}
                    onChange={handleChange}
                    placeholder="Dietary preferences (optional)"
                    className="appearance-none block w-full pl-4 pr-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Total Amount</span>
                  <span className="text-2xl font-bold text-primary-600">
                    {registrationFee > 0 ? `₹${registrationFee.toLocaleString('en-IN')}` : 'Free'}
                  </span>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={submitting || paymentStatus === 'redirecting'}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg font-semibold text-base transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Processing Registration...
                  </>
                ) : paymentStatus === 'redirecting' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {getText('eventRegistration.redirectingToPayment')}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    {registrationFee > 0 ? getText('eventRegistration.registerAndPay') : getText('eventRegistration.completeRegistration')}
                  </>
                )}
              </button>
              <p className="mt-4 text-center text-xs text-gray-500 leading-relaxed">
                By submitting this form, you agree to our{' '}
                <a href="/terms-conditions" className="text-primary-600 hover:text-primary-700 underline">
                  Terms &amp; Conditions
                </a>
                ,{' '}
                <a href="/privacy-policy" className="text-primary-600 hover:text-primary-700 underline">
                  Privacy Policy
                </a>
                {registrationFee > 0 && (
                  <>
                    {' and '}
                    <a href="/refund-policy" className="text-primary-600 hover:text-primary-700 underline">
                      Refund Policy
                    </a>
                  </>
                )}
                . Your information will be used solely for event registration purposes.
              </p>
            </div>
          </form>
        </div>
        )}
      </div>
    </div>
  );
};

export default EventRegistrationPage;
